import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmationModal } from '@components';
import HorizontalStepper from '@components/horizontal-stepper/HorizontalStepper';
import { GROUPS } from '@constants/routes';
import {
  Event,
  MixpanelEventV2,
  sendMixpanelEvent,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import BasicStep from '@features/group-management/components/v2/create-group/BasicStep';
import MemberStep from '@features/group-management/components/v2/create-group/MemberStep';
import { getGroupFeeCollectionMethodType } from '@features/group-management/helper';
import useCreateGroupForm from '@features/group-management/hooks/useCreateGroupForm';

import CreateGroupHeader from './CreateGroupHeader';

const CreateGroup = () => {
  const [step, setStep] = useState<'basic' | 'member'>('basic');
  const navigate = useNavigate();

  const {
    formData,
    onInputChange,
    memberList,
    isFeeCategoryEnabled,
    setIsFeeCategoryEnabled,
    feeCategories,
    setFeeCategories,
    branchDetails,
    customTermDueDateError,
    setCustomTermDueDateError,
    feeDetailsError,
    setFeeDetailsError,
    bulkPaymentDiscountRowErrors,
    setBulkPaymentDiscountRowErrors,
    isSaveDisabled,
    isLoading,
    onSaveNewGroup,
    hasGroupAttendanceEditAccess,
    handleAttendanceSwitchClick,
    createdGroupId,
    isAttendanceBasedFee
  } = useCreateGroupForm();

  const [memberStepState, setMemberStepState] = useState({
    onSave: () => {},
    isSaveDisabled: true,
    isLoading: false
  });

  const memberStepStateRef = useRef(memberStepState);
  memberStepStateRef.current = memberStepState;

  const handleMemberHeaderStateChange = useCallback(
    (state: {
      onSave: () => void;
      isSaveDisabled: boolean;
      isLoading: boolean;
    }) => {
      setMemberStepState(state);
    },
    []
  );

  const handleBasicSave = async () => {
    const groupId = await onSaveNewGroup();
    if (groupId) {
      setStep('member');
    }
  };

  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [discardLeaveKind, setDiscardLeaveKind] = useState<
    'basic' | 'member' | null
  >(null);

  const hasBasicStepData =
    !!formData.groupName.trim() ||
    !!formData.amount ||
    !!formData.paymentCollectionType ||
    !!formData.activationDate;

  const pendingNavigationRef = useRef<string | null>(null);

  const handleBackNavigation = useCallback(() => {
    sendMixpanelEvent(Event.GROUP_CREATION_CANCEL_CLICKED, {
      group_type: getGroupFeeCollectionMethodType(isAttendanceBasedFee || false)
    });
    if (step === 'basic') {
      sendMixpanelEventV2(MixpanelEventV2.CREATE_GROUP_CANCELLED);
    }
    const memberTargetPath = createdGroupId
      ? `${GROUPS.HOME}/${createdGroupId}`
      : GROUPS.HOME;

    if (step === 'basic' && hasBasicStepData) {
      pendingNavigationRef.current = GROUPS.HOME;
      setDiscardLeaveKind('basic');
      setShowDiscardModal(true);
      return;
    }

    if (step === 'member') {
      pendingNavigationRef.current = memberTargetPath;
      setDiscardLeaveKind('member');
      setShowDiscardModal(true);
      return;
    }

    navigate(GROUPS.HOME);
  }, [step, hasBasicStepData, createdGroupId, navigate, isAttendanceBasedFee]);

  const handleConfirmDiscard = () => {
    setShowDiscardModal(false);
    setDiscardLeaveKind(null);
    const target = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    if (target) {
      navigate(target);
    }
  };

  const handleDismissDiscardModal = () => {
    setShowDiscardModal(false);
    setDiscardLeaveKind(null);
    pendingNavigationRef.current = null;
  };

  useEffect(() => {
    if (step !== 'basic' || !hasBasicStepData) return undefined;

    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      pendingNavigationRef.current = GROUPS.HOME;
      setDiscardLeaveKind('basic');
      setShowDiscardModal(true);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [step, hasBasicStepData]);

  useEffect(() => {
    if (step !== 'member' || !createdGroupId) return undefined;

    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      pendingNavigationRef.current = `${GROUPS.HOME}/${createdGroupId}`;
      setDiscardLeaveKind('member');
      setShowDiscardModal(true);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [step, createdGroupId]);

  const steps = [{ label: 'Basic details' }, { label: 'Add members' }];
  const currentStep = step === 'basic' ? 0 : 1;

  const headerOnSave =
    step === 'basic' ? handleBasicSave : memberStepState.onSave;
  const headerIsSaveDisabled =
    step === 'basic' ? isSaveDisabled : memberStepState.isSaveDisabled;
  const headerIsLoading =
    step === 'basic' ? isLoading : memberStepState.isLoading;

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-cofeeGreyBlue1">
      <CreateGroupHeader
        step={step}
        onSave={headerOnSave}
        isSaveDisabled={headerIsSaveDisabled}
        isLoading={headerIsLoading}
        onBack={handleBackNavigation}
      />
      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        <div className="mx-auto mb-8 w-1/2 min-w-90 p-8">
          <HorizontalStepper steps={steps} currentStep={currentStep} />
        </div>
        <div className="mx-auto w-5/6 min-w-120 pb-8">
          {step === 'basic' && (
            <BasicStep
              formData={formData}
              onInputChange={onInputChange}
              memberList={memberList}
              isFeeCategoryEnabled={isFeeCategoryEnabled}
              setIsFeeCategoryEnabled={setIsFeeCategoryEnabled}
              feeCategories={feeCategories}
              setFeeCategories={setFeeCategories}
              branchDetails={branchDetails}
              customTermDueDateError={customTermDueDateError}
              setCustomTermDueDateError={setCustomTermDueDateError}
              feeDetailsError={feeDetailsError}
              setFeeDetailsError={setFeeDetailsError}
              bulkPaymentDiscountRowErrors={bulkPaymentDiscountRowErrors}
              onClearBulkPaymentDiscountRowErrors={() =>
                setBulkPaymentDiscountRowErrors([])
              }
              hasGroupAttendanceEditAccess={hasGroupAttendanceEditAccess}
              handleAttendanceSwitchClick={handleAttendanceSwitchClick}
              isLoading={isLoading}
              isAttendanceBasedFee={isAttendanceBasedFee}
            />
          )}
          {step === 'member' && createdGroupId && (
            <MemberStep
              groupId={createdGroupId}
              onHeaderStateChange={handleMemberHeaderStateChange}
              defaultTermLabel={branchDetails?.default_term_label}
            />
          )}
        </div>
      </div>
      {showDiscardModal && (
        <ConfirmationModal
          isOpen={showDiscardModal}
          handleConfirm={handleConfirmDiscard}
          handleCancel={handleDismissDiscardModal}
          title={
            discardLeaveKind === 'member'
              ? 'Leave add members?'
              : 'Unsaved Changes'
          }
          message={
            discardLeaveKind === 'member'
              ? 'Are you sure you want to leave? Any members you have not saved will be lost.'
              : 'Are you sure that you want to leave the current page? The changes that you made will not be saved.'
          }
          className="w-96"
        />
      )}
    </div>
  );
};

export default CreateGroup;
